// FirmwareDetail — the shareable per-build page at /firmware/:hash.
//
// The :hash is a git hash OR a sha256 prefix (>=7 chars); the server resolves
// either, including the sha256 of ANY of the build's target files. This is the
// canonical home for a build's comment thread, and — post-008 — the management
// surface for its target files: add a target, edit a target's architecture and
// models, attach/replace/remove a signing file, remove a target.
// Same access gate as the hub (pilot | operations | manufacturing | admin).

import React, { useCallback, useEffect, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import Field from '../components/ui/Field';
import Checkbox from '../components/ui/Checkbox';
import LogoSpinner from '../components/ui/LogoSpinner';
import FadeIn from '../components/ui/FadeIn';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import FilePick from '../components/ui/FilePick';
import { useParams, useHistory } from 'react-router-dom';
import BackLink from '../components/BackLink';
import useUserStore from '../stores/useUserStore';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { useFirmwareApi, formatBytes, shortSha, formatUploaded, firmwareShortHash, firmwareDeleteMessage, targetName } from './firmwareApi';
import FirmwareComments from './FirmwareComments';
import UploadFirmwareModal from './UploadFirmwareModal';
import { CATEGORY_LABELS } from './types';
import type { Firmware, FirmwareFile, FirmwareModel, ArchPreset } from './types';
import PageLayout from '../components/ui/PageLayout';

const Copyable: React.FC<{ value: string | null; label?: string; mono?: boolean }> = ({ value, label, mono = true }) => {
  const toast = useToast();
  if (!value) return <span className="text-on-surface-disabled">—</span>;
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label || 'Value'} copied`); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className={`text-on-surface break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
      <button onClick={copy} className={`${buttonClass('quiet', 'sm')} flex-none`}>copy</button>
    </span>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-[7.5rem_1fr] gap-3 py-2 border-b border-gray-800/60 text-sm">
    <span className="text-on-surface-variant">{label}</span>
    <div className="text-on-surface min-w-0">{children}</div>
  </div>
);

const StatusBadge: React.FC<{ fw: Firmware }> = ({ fw }) => {
  if (fw.is_archived) return <Tag>Retired</Tag>;
  if (fw.is_active) return <Tag tone="good">Active</Tag>;
  return <Tag>Inactive</Tag>;
};

// ── one target file ────────────────────────────────────────────────────────

interface TargetCardProps {
  file: FirmwareFile;
  models: FirmwareModel[];
  archPresets: ArchPreset[];
  canManage: boolean;
  canRemove: boolean; // false on the build's only file (the server refuses it too)
  nameOf: (k: string) => string;
  onChanged: () => void;
}

const TargetFileCard: React.FC<TargetCardProps> = ({ file, models, archPresets, canManage, canRemove, nameOf, onChanged }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const fw = useFirmwareApi();

  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [arch, setArch] = useState(file.architecture || '');
  const [sel, setSel] = useState<string[]>(file.applicable_models || []);

  // okMsg omitted = no toast and no refetch (downloads: the browser's own download
  // indicator is the feedback, and nothing on the server changed).
  const run = async (fn: () => Promise<unknown>, okMsg?: string) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) { toast.success(okMsg); onChanged(); }
    } catch (e: any) { toast.error(e?.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const onPickSig = (f: File | null) => {
    if (!f) return;
    run(() => fw.uploadSignature(file.id, f), file.has_signature ? 'Signing file replaced' : 'Signing file attached');
  };

  const onRemoveSig = async () => {
    const ok = await confirm({
      title: 'Remove signing file',
      tone: 'danger',
      confirmLabel: 'Remove signature',
      message: `${file.sig_file_name}\n\nThe firmware file itself stays published. You can attach a signature again later.`,
    });
    if (ok) run(() => fw.removeSignature(file.id), 'Signing file removed');
  };

  const onRemoveTarget = async () => {
    const ok = await confirm({
      title: 'Remove this target file',
      tone: 'danger',
      confirmLabel: 'Remove target',
      message: `${targetName(file)}\n${file.file_name}\n\nThis removes the file${file.has_signature ? ' and its signature' : ''} from the build. Other targets, the build record, and the comment thread are untouched.`,
    });
    if (ok) run(() => fw.removeFile(file.id), 'Target file removed');
  };

  return (
    <Panel label={targetName(file)} padding="sm" className="mb-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white font-mono break-all">{targetName(file)}</span>
            {file.has_signature ? (
              <Tag tone="good" size="sm">signed</Tag>
            ) : (
              <Tag size="sm">unsigned</Tag>
            )}
          </div>
          <div className="text-[11px] text-on-surface-variant mt-1 font-mono break-all">
            {file.file_name} <span className="text-on-surface-variant">· {formatBytes(file.file_size_bytes)}</span>
          </div>
          {file.applicable_models && file.applicable_models.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {file.applicable_models.map((k) => (
                <Tag key={k} tone="info" size="sm">{nameOf(k)}</Tag>
              ))}
            </div>
          )}
          <div className="text-[11px] text-on-surface-variant mt-2">
            <span className="text-on-surface-variant">SHA-256 </span>
            <Tag variant="code" mono size="sm" title={file.sha256 || undefined}>{shortSha(file.sha256)}</Tag>
          </div>
          {file.uploaded_at && (
            <div className="text-[11px] text-on-surface-variant mt-1">
              uploaded {formatUploaded(file.uploaded_at)}{file.uploaded_by_name ? ` by ${file.uploaded_by_name}` : ''}
            </div>
          )}
        </div>

        <div className="flex-none flex flex-col items-end gap-1.5">
          <button
            onClick={() => run(() => fw.downloadFile(file))}
            disabled={busy}
            className={buttonClass('filled', 'md')}
          >
            ↓ Download
          </button>
          {file.has_signature && (
            <Tooltip label={`${file.sig_file_name} · ${formatBytes(file.sig_file_size_bytes)}`}>
              <button
                onClick={() => run(() => fw.downloadSignature(file))}
                disabled={busy}
                className={buttonClass('quiet', 'sm')}
              >
                ↓ .sig
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Signature detail */}
      <Reveal show={!!file.has_signature}>
        <div className="mt-2.5 pt-2 border-t border-gray-800/60 text-[11px] text-on-surface-variant font-mono break-all">
          {file.sig_file_name}
          <span className="text-on-surface-variant font-sans">
            {' · '}{formatBytes(file.sig_file_size_bytes)}
            {file.sig_uploaded_at ? ` · attached ${formatUploaded(file.sig_uploaded_at)}` : ''}
            {file.sig_uploaded_by_name ? ` by ${file.sig_uploaded_by_name}` : ''}
          </span>
        </div>
      </Reveal>

      {/* Manage row */}
      {canManage && (
        <div className="mt-3 pt-2.5 border-t border-gray-800/60 flex items-center gap-3 flex-wrap">
          <FilePick accept=".sig" variant="quiet" size="sm" disabled={busy} onFiles={(f) => onPickSig(f[0] || null)}>
            {file.has_signature ? 'Replace signing file' : 'Attach signing file'}
          </FilePick>
          {file.has_signature && (
            <button onClick={onRemoveSig} disabled={busy} className={buttonClass('danger-quiet', 'sm')}>Remove signature</button>
          )}
          {/* Re-seed from the file on OPEN. useState only reads its initializer on
              mount, so without this an abandoned edit (Cancel, or another user's
              change after a refetch) would still be sitting in the fields when the
              editor is reopened. */}
          <button
            onClick={() => setEditing((v) => {
              if (!v) { setArch(file.architecture || ''); setSel(file.applicable_models || []); }
              return !v;
            })}
            disabled={busy}
            className={buttonClass('quiet', 'sm')}
          >
            {editing ? 'Cancel' : 'Edit target'}
          </button>
          {canRemove && (
            <button onClick={onRemoveTarget} disabled={busy} className={`${buttonClass('danger-quiet', 'sm')} ml-auto`}>Remove target</button>
          )}
        </div>
      )}

      {/* Inline per-target editor: architecture + models are per FILE */}
      <Reveal show={!!(canManage && editing)}>
        <Panel label="Edit target" on="panel" padding="sm" className="mt-3 space-y-3">
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">Architecture <span className="text-on-surface-variant">(the build target)</span></label>
            <Field
              className="font-mono"
              list="fw-detail-arch-presets"
              value={arch}
              maxLength={120}
              onChange={(e) => setArch(e.target.value)}
              placeholder="e.g. ark_pi6x_default"
              disabled={busy}
            />
            <datalist id="fw-detail-arch-presets">
              {archPresets.map((p) => (<option key={p.token} value={p.arch}>{p.token}</option>))}
            </datalist>
            <p className="mt-1 text-[11px] text-on-surface-variant">
              The filename was fixed at upload, so renaming the target here does not rename the stored file.
            </p>
          </div>
          <div>
            <label className="block text-xs text-on-surface-variant mb-1.5">Applies to <span className="text-on-surface-variant">(which drones flash this file)</span></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {models.map((m) => (
                <Checkbox
                  key={m.key}
                  label={m.name}
                  checked={sel.includes(m.key)}
                  onChange={() => setSel((p) => (p.includes(m.key) ? p.filter((k) => k !== m.key) : [...p, m.key]))}
                  disabled={busy}
                />
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => run(async () => {
                await fw.updateFile(file.id, { architecture: arch.trim() || null, applicable_models: sel });
                setEditing(false);
              }, 'Target updated')}
              disabled={busy || sel.length === 0}
              className={buttonClass('filled', 'md')}
            >
              Save target
            </button>
          </div>
        </Panel>
      </Reveal>
    </Panel>
  );
};

// ── page ───────────────────────────────────────────────────────────────────

const FirmwareDetail: React.FC = () => {
  // `slug` is present only on the namespaced route (/firmware/dronevis/:hash) —
  // it scopes the resolver to that category so a short vision sha can never land
  // on a px4 build that happens to share the prefix.
  const { slug, hash } = useParams<{ slug?: string; hash: string }>();
  const history = useHistory();
  const toast = useToast();
  const confirm = useConfirm();
  const fw = useFirmwareApi();
  const hasCapability = useUserStore((s) => s.hasCapability);
  const canManage = hasCapability('manufacturing');
  const canDelete = hasCapability('manager');

  const [item, setItem] = useState<Firmware | null>(null);
  const [models, setModels] = useState<FirmwareModel[]>([]);
  const [archPresets, setArchPresets] = useState<ArchPreset[]>([]);
  const [published, setPublished] = useState<Firmware[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingTarget, setAddingTarget] = useState(false);

  const nameOf = useCallback((k: string) => models.find((m) => m.key === k)?.name || k, [models]);

  // Initial load: resolve the row by hash + grab the model/preset lists (for the
  // edit modal and model-name display).
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      const [resolved, list] = await Promise.allSettled([fw.resolveByHash(hash, slug), fw.list()]);
      if (!alive) return;
      if (list.status === 'fulfilled') {
        setModels(list.value.models || []);
        setArchPresets(list.value.archPresets || []);
        setPublished(Object.values(list.value.categories).flat());
      }
      if (resolved.status === 'fulfilled') setItem(resolved.value);
      else setNotFound(true);
      setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, hash]);

  // After a mutation, re-pull the row from the list by id (robust even if an edit
  // changed the git hash, which would shift the URL's hash).
  const reload = useCallback(async () => {
    if (!item) return;
    try {
      const list = await fw.list();
      const all = Object.values(list.categories).flat();
      setPublished(all);
      const fresh = all.find((f) => f.id === item.id);
      if (fresh) setItem(fresh);
      else { setItem(null); setNotFound(true); }
    } catch {
      /* leave the current view in place on a transient failure */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const mutate = async (patch: Parameters<typeof fw.update>[1], okMsg: string) => {
    if (!item) return;
    setBusy(true);
    try { await fw.update(item.id, patch); toast.success(okMsg); await reload(); }
    catch (e: any) { toast.error(e?.message || 'Update failed'); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!item) return;
    const ok = await confirm({
      title: 'Delete firmware permanently',
      tone: 'danger',
      confirmLabel: 'Delete permanently',
      message: firmwareDeleteMessage(item),
    });
    if (!ok) return;
    setBusy(true);
    try { await fw.remove(item.id); toast.success('Firmware deleted'); history.push('/firmware'); }
    catch (e: any) { toast.error(e?.message || 'Delete failed'); setBusy(false); }
  };

  const files = item?.files || [];

  return (
    <PageLayout width="narrow" kind="record" className="pt-4">
      <BackLink to="/firmware" label="Firmware" className="mb-4" />

      {loading ? (
        <LogoSpinner label="Loading the build" />
      ) : notFound || !item ? (
        <FadeIn>
          <EmptyState
            title={<>No build matches “{hash}”.</>}
            action={<BackLink to="/firmware" label="Firmware" />}
          >
            It may have been deleted, or the hash is off. Try at least 7 characters.
          </EmptyState>
        </FadeIn>
      ) : (
        <FadeIn>
          {/* Header — the BUILD (one commit) */}
          <div className="mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge fw={item} />
              <span className="text-[10px] text-on-surface-variant uppercase tracking-wide">{CATEGORY_LABELS[item.category]}</span>
              {files.length > 1 && (
                <Tag size="sm">{files.length} targets</Tag>
              )}
            </div>
            <h1 className="text-2xl font-bold text-white mt-1 break-words">{item.label}</h1>
            <p className="text-sm text-on-surface-variant font-mono mt-0.5">#{firmwareShortHash(item)}</p>
          </div>

          {/* Build metadata — everything that belongs to the commit */}
          <Panel label="Build details" padding="none" className="mb-5 px-4 py-1.5">
            <Row label="Version">{item.version || <span className="text-on-surface-disabled">—</span>}</Row>
            <Row label="Branch">{item.branch || <span className="text-on-surface-disabled">—</span>}</Row>
            <Row label="Git hash"><Copyable value={item.git_hash} label="Git hash" /></Row>
            <Row label="Released">{item.release_date || <span className="text-on-surface-disabled">—</span>}</Row>
            <Row label="Uploaded">
              {item.uploaded_at ? `${formatUploaded(item.uploaded_at)}${item.uploaded_by_name ? ` by ${item.uploaded_by_name}` : ''}` : <span className="text-on-surface-disabled">—</span>}
            </Row>
            <Row label="Share">
              <button
                onClick={async () => {
                  try { await navigator.clipboard.writeText(window.location.href); toast.success('Link copied'); }
                  catch { toast.error('Copy failed'); }
                }}
                className={buttonClass('quiet', 'sm')}
              >
                Copy link
              </button>
            </Row>
            {item.notes && (
              <Row label="Notes"><p className="whitespace-pre-wrap text-on-surface">{item.notes}</p></Row>
            )}
          </Panel>

          {/* Target files — architecture, models, the bytes, and the signing file
              all belong to the FILE, not the commit. */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                {files.length === 1 ? 'File' : `Target files (${files.length})`}
              </h2>
              {canManage && (
                <button
                  onClick={() => setAddingTarget(true)}
                  disabled={busy}
                  className={buttonClass('quiet', 'sm')}
                >
                  + Add target file
                </button>
              )}
            </div>
            {files.length === 0 ? (
              <EmptyState variant="area">No files on this build.</EmptyState>
            ) : (
              files.map((f) => (
                <TargetFileCard
                  key={f.id}
                  file={f}
                  models={models}
                  archPresets={archPresets}
                  canManage={canManage}
                  canRemove={files.length > 1}
                  nameOf={nameOf}
                  onChanged={reload}
                />
              ))
            )}
          </div>

          {/* Comment thread — the headline of this page */}
          <div className="mb-5">
            <h2 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-2">Discussion</h2>
            <FirmwareComments key={item.id} firmwareId={item.id} count={item.comment_count || 0} defaultOpen />
          </div>

          {/* Manage actions — build level */}
          {(canManage || canDelete) && (
            <div className="flex items-center gap-4 pt-3 border-t border-gray-800">
              {canManage && (
                <>
                  <button onClick={() => setEditing(true)} disabled={busy} className={buttonClass('quiet', 'sm')}>Edit</button>
                  {item.is_archived ? (
                    <button onClick={() => mutate({ is_archived: false, is_active: true }, 'Re-activated')} disabled={busy} className={buttonClass('quiet', 'sm')}>Re-activate</button>
                  ) : (
                    <>
                      <Appear show={!item.is_active} as="div">
                        <button onClick={() => mutate({ is_active: true }, 'Activated')} disabled={busy} className={buttonClass('quiet', 'sm')}>Activate</button>
                      </Appear>
                      <button onClick={() => mutate({ is_archived: true }, 'Retired')} disabled={busy} className={buttonClass('quiet', 'sm')}>Retire</button>
                    </>
                  )}
                </>
              )}
              {canDelete && (
                <button onClick={onDelete} disabled={busy} className={`${buttonClass('danger-quiet', 'sm')} ml-auto`}>Delete</button>
              )}
            </div>
          )}

          {editing && (
            <UploadFirmwareModal
              mode="edit"
              category={item.category}
              models={models}
              archPresets={archPresets}
              existing={item}
              onClose={() => setEditing(false)}
              onSaved={reload}
            />
          )}

          {addingTarget && (
            <UploadFirmwareModal
              mode="addTarget"
              category={item.category}
              models={models}
              archPresets={archPresets}
              existing={item}
              published={published}
              onClose={() => setAddingTarget(false)}
              onSaved={reload}
            />
          )}
        </FadeIn>
      )}
    </PageLayout>
  );
};

export default FirmwareDetail;
